import React, { useState } from 'react'
import { FiX, FiCheck, FiLoader } from 'react-icons/fi'
import api from '../config/axios'
import { useAuthStore } from '../store/authStore'
import { useSearchStore } from '../store/searchStore'

const ORIENTATION_NAMES = [
  'Original', 'Swap W and D', 'Swap L and W',
  'Rotate: L→D→W→L', 'Rotate: L→W→D→L', 'All reversed'
]

function DrawingModal({ result, onClose, onSuccess }) {
  const { user } = useAuthStore()
  const { searchedDimensions } = useSearchStore()
  const [status, setStatus] = useState('idle') // idle | loading | done | error
  const fmt = v => v != null ? parseFloat(parseFloat(v).toFixed(2)) : '—'

  const orientationLabel = result.orientationIndex != null
    ? (ORIENTATION_NAMES[result.orientationIndex] ?? `Orientation ${result.orientationIndex + 1}`)
    : (result.orientation || '—')

  const handleConfirm = async () => {
    setStatus('loading')
    try {
      await api.post('/api/auth/request-drawing', {
        user: {
          uid: user?.uid,
          name: user?.displayName,
          displayName: user?.displayName,
          companyName: user?.companyName,
          email: user?.email,
          cellNumber: user?.cellNumber,
        },
        searchedDimensions,
        case: {
          model: result.model,
          series: result.series,
          manufacturer: result.manufacturer,
          length: result.length,
          width: result.width,
          depth: result.depth,
          lidDepth: result.lidDepth,
          baseDepth: result.baseDepth,
          volumeRatio: result.volumeRatio,
          orientation: orientationLabel,
        }
      })
      setStatus('done')
      onSuccess(result.model)
    } catch (err) {
      console.error('Drawing request failed:', err)
      setStatus('error')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70" onClick={status === 'loading' ? undefined : onClose} />

      <div className="relative bg-bg-card border border-border-dark rounded-xl shadow-2xl w-full max-w-md p-6 space-y-5">

        {/* Close button */}
        {status !== 'loading' && (
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-white transition">
            <FiX size={20} />
          </button>
        )}

        {status === 'done' ? (
          <div className="text-center py-4 space-y-3">
            <div className="w-14 h-14 rounded-full bg-accent-teal/20 border border-accent-teal flex items-center justify-center mx-auto">
              <FiCheck size={28} className="text-accent-teal" />
            </div>
            <h3 className="text-xl font-bold text-white">Drawing Request Sent</h3>
            <p className="text-gray-400 text-sm">
              We've received your drawing request for the <span className="text-accent-yellow font-semibold">{result.model}</span>.
              Our team will be in touch with you.
            </p>
            <button onClick={onClose} className="w-full btn-primary mt-2">Close</button>
          </div>
        ) : (
          <>
            <div>
              <h3 className="text-xl font-bold text-accent-yellow">Request a Drawing</h3>
              <p className="text-gray-400 text-sm mt-1">
                Confirm below and we'll be notified immediately of your drawing request.
              </p>
            </div>

            {/* Case details */}
            <div className="bg-bg-dark rounded-lg p-4 space-y-2 text-sm">
              <p className="text-xs font-semibold text-accent-teal uppercase tracking-widest mb-2">Case Details</p>
              <div className="flex justify-between">
                <span className="text-gray-400">Model</span>
                <span className="text-accent-yellow font-bold">{result.model}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Series</span>
                <span className="text-white">{result.series}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Manufacturer</span>
                <span className="text-white">{result.manufacturer}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Internal dims</span>
                <span className="text-white">L {fmt(result.length)} × W {fmt(result.width)} × D {fmt(result.depth)} cm</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Orientation</span>
                <span className="text-white text-xs">{orientationLabel}</span>
              </div>
              {searchedDimensions && (
                <div className="flex justify-between border-t border-border-dark pt-2 mt-1">
                  <span className="text-gray-400">Your item dims</span>
                  <span className="text-white">
                    L {fmt(searchedDimensions.length)} × W {fmt(searchedDimensions.width)} × D {fmt(searchedDimensions.depth)} cm
                  </span>
                </div>
              )}
            </div>

            {/* User details */}
            <div className="bg-bg-dark rounded-lg p-4 space-y-2 text-sm">
              <p className="text-xs font-semibold text-accent-teal uppercase tracking-widest mb-2">Your Details</p>
              <div className="flex justify-between">
                <span className="text-gray-400">Name</span>
                <span className="text-white">{user?.displayName || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Company</span>
                <span className="text-white">{user?.companyName || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Email</span>
                <span className="text-white">{user?.email || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Cell</span>
                <span className="text-white">{user?.cellNumber || '—'}</span>
              </div>
            </div>

            {status === 'error' && (
              <p className="text-red-400 text-sm text-center">
                Something went wrong. Please try again or contact us directly.
              </p>
            )}

            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={status === 'loading'}
                className="flex-1 px-4 py-2 border border-border-dark text-gray-400 hover:text-white hover:border-gray-400 rounded transition text-sm disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={status === 'loading'}
                className="flex-1 btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {status === 'loading' ? (
                  <><FiLoader size={15} className="animate-spin" /> Sending…</>
                ) : (
                  'Confirm Request'
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default DrawingModal
